'use client';

import { ReactNode, SyntheticEvent, useCallback, useEffect } from 'react';

import { clsx } from 'clsx';

import { Portal } from '@shared/ui/Portal';
import { addPadding, removePadding } from '@shared/utils/scrollHelpers/scrollHelpers';

import cls from './Modal.module.scss';

export type ModalProps = {
  isOpen: boolean;
  children: ReactNode;
  onClose: () => void;
  fullScreen?: boolean;
  className?: string;
  contentClassName?: string;
  withoutStopPropagation?: boolean;
};

export const Modal = (props: ModalProps) => {
  const { isOpen, onClose, children, fullScreen, contentClassName, className, withoutStopPropagation } = props;

  const onContentClick = (e: SyntheticEvent) => {
    if (!withoutStopPropagation) e.stopPropagation();
  };

  const onKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    },
    [onClose]
  );

  useEffect(() => {
    window.addEventListener('keydown', onKeyDown);

    if (isOpen) {
      addPadding();
    }

    return () => {
      window.removeEventListener('keydown', onKeyDown);
      removePadding();
    };
  }, [isOpen, onKeyDown]);

  return (
    <Portal>
      <div
        data-testid="modal-overlay"
        onClick={onClose}
        className={clsx(className, cls.wrap)}
      >
        <div
          data-style={!fullScreen ? 'padding-lock' : 'N'}
          onClick={onContentClick}
          className={clsx(cls.content, contentClassName)}
        >
          {children}
        </div>
      </div>
    </Portal>
  );
};
